import { Link } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  productionUiDisplayStateSelector,
  productionUiStateSelector,
  resolveProductionProgressTitle,
  resolveRydeProgressColor,
} from "@ryde/shared";

import { RydeProgress } from "../../progress";
import { ProductionPreviewProgressEndAdornment } from "./production-preview-progress-end-adornment";

interface IProductionPreviewProgressProps {
  isStarted: boolean;
  isAborting: boolean;
  isOtherProductionInProgress: boolean;
  handleProductionAbort: () => void;
  handlePreviousProductionClick: () => void;
}

export const ProductionPreviewProgress: FC<IProductionPreviewProgressProps> = ({
  isStarted,
  isAborting,
  isOtherProductionInProgress,
  handleProductionAbort,
  handlePreviousProductionClick,
}) => {
  const { t } = useTranslation();

  const productionUiState = useRecoilValue(productionUiStateSelector);
  const { logsCount } = productionUiState || {};

  const { loadPercentage, isFinished, isAborted, error } = useRecoilValue(
    productionUiDisplayStateSelector,
  );

  const rydeProgressTitle = resolveProductionProgressTitle({
    isFinished: Boolean(isFinished),
    isAborted: Boolean(isAborted),
    isAborting,
    error,
    t,
  });
  const rydeProgressColor = resolveRydeProgressColor({
    isFinished: Boolean(isFinished),
    error,
    isAborting,
  });

  return (
    <>
      {isStarted ? (
        <RydeProgress
          value={loadPercentage}
          displayValue={logsCount ? `logs:${logsCount}` : undefined}
          title={rydeProgressTitle}
          color={rydeProgressColor}
          isLoading={!isFinished && !isAborted && !error}
          EndAdornment={
            <ProductionPreviewProgressEndAdornment
              isAbort={!isFinished && !isAborted && !error && !isAborting}
              onAbort={handleProductionAbort}
            />
          }
        />
      ) : (
        <>
          <RydeProgress value={0} title={t("rideProduction.progress")} />{" "}
          {isOtherProductionInProgress && (
            <p>
              {t("rideProduction.anotherProductionFirst")}
              <Link
                className="pointer production-preview__link"
                onClick={handlePreviousProductionClick}
                underline="always"
              >
                {t("rideProduction.anotherProductionSecond")}
              </Link>
              {t("rideProduction.anotherProductionThird")}
            </p>
          )}
        </>
      )}
    </>
  );
};
